import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

interface StepCompanyIdentityProps {
  data: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
  errors: Record<string, string>;
}

export default function StepCompanyIdentity({ data, onChange, errors }: StepCompanyIdentityProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';

  const fields = [
    { key: 'name', labelKey: 'ob_s1_name', phKey: 'ob_s1_name_ph', required: true, fullWidth: true },
    { key: 'shortName', labelKey: 'ob_s1_shortName', phKey: 'ob_s1_shortName_ph', required: true },
    { key: 'edrpou', labelKey: 'ob_s1_edrpou', phKey: 'ob_s1_edrpou_ph', required: true },
    { key: 'taxId', labelKey: 'ob_s1_taxId', phKey: 'ob_s1_taxId_ph', required: false },
    { key: 'country', labelKey: 'ob_s1_country', phKey: 'ob_s1_country_ph', required: true },
  ];

  const handleChange = (key: string, value: string) => {
    onChange({ ...data, [key]: value });
  };
  const handleBlur = (key: string) => {
    setTouched({ ...touched, [key]: true });
  };

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h2 className="text-xl font-semibold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          {t('ob_s1_title')}
        </h2>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {t('ob_s1_subtitle')}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((field) => (
          <div key={field.key} className={field.fullWidth ? 'md:col-span-2' : ''}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
              {t(field.labelKey)}
              {field.required && <span style={{ color: 'var(--accent)' }}>*</span>}
            </label>
            <input
              type="text"
              value={data[field.key] ?? ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onBlur={() => handleBlur(field.key)}
              placeholder={t(field.phKey)}
              className="w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none"
              style={{
                borderColor: touched[field.key] && errors[field.key] ? 'var(--accent)' : inputBorder,
                background: inputBg,
                color: textMain,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
            />
            {touched[field.key] && errors[field.key] && (
              <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}